import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActionArea,
  Box,
  Typography,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Star as StarIcon,
  BugReport as BugReportIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  PlayCircle as PlayCircleIcon,
} from '@mui/icons-material';
import { Tool } from '../../../../types/navigation';
import { YouTubeVideoDialog } from '../../../common/YouTubeVideoDialog';

interface ToolCardProps {
  tool: Tool;
  onSelect: (toolId: string) => void;
  variant?: 'default' | 'compact' | 'featured';
  showDescription?: boolean;
  showStatus?: boolean;
  className?: string;
}

/**
 * Mobile-optimized tool card component
 * Features touch-friendly design with 44px minimum touch targets
 */
export const ToolCard: React.FC<ToolCardProps> = ({
  tool,
  onSelect,
  variant = 'default',
  showDescription = true,
  showStatus = true,
  className,
}) => {
  const isDisabled = tool.status === 'coming_soon';
  const ToolIcon = tool.icon;
  const [videoMenuAnchor, setVideoMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [selectedVideo, setSelectedVideo] = useState<{
    videoId: string;
    title: string;
  } | null>(null);

  const hasVideos = tool.tutorialVideos && tool.tutorialVideos.length > 0;

  const getStatusConfig = (status: Tool['status']) => {
    switch (status) {
      case 'available':
        return {
          color: 'success' as const,
          label: 'Available',
          icon: CheckCircleIcon,
        };
      case 'beta':
        return {
          color: 'warning' as const,
          label: 'Beta',
          icon: BugReportIcon,
        };
      case 'coming_soon':
        return {
          color: 'info' as const,
          label: 'Coming Soon',
          icon: ScheduleIcon,
        };
      case 'premium':
        return {
          color: 'primary' as const,
          label: 'Premium',
          icon: StarIcon,
        };
      default:
        return {
          color: 'default' as const,
          label: 'Unknown',
          icon: InfoIcon,
        };
    }
  };

  const statusConfig = getStatusConfig(tool.status);
  const StatusIcon = statusConfig.icon;

  const handleClick = () => {
    if (!isDisabled) {
      onSelect(tool.id);
    }
  };

  const handleVideoButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!tool.tutorialVideos) return;

    if (tool.tutorialVideos.length === 1) {
      // Single video - open directly
      setSelectedVideo({
        videoId: tool.tutorialVideos[0].videoId,
        title: tool.tutorialVideos[0].title,
      });
    } else {
      // Multiple videos - show menu
      setVideoMenuAnchor(event.currentTarget);
    }
  };

  const handleVideoMenuClose = () => {
    setVideoMenuAnchor(null);
  };

  const handleVideoSelect = (videoId: string, title: string) => {
    setSelectedVideo({ videoId, title });
    setVideoMenuAnchor(null);
  };

  const handleVideoDialogClose = () => {
    setSelectedVideo(null);
  };

  const cardHeight = variant === 'compact' ? 100 : 120;

  return (
    <Card
      className={className}
      sx={{
        height: cardHeight,
        display: 'flex',
        flexDirection: 'column',
        opacity: isDisabled ? 0.6 : 1,
        border: '2px solid',
        borderColor: 'divider',
        boxShadow: 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: isDisabled ? 'none' : 'translateY(-2px)',
          boxShadow: isDisabled ? 1 : 3,
        },
      }}
    >
      <CardActionArea
        onClick={handleClick}
        disabled={isDisabled}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          minHeight: 44, // WCAG touch target minimum
          p: 1.5,
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            '&:last-child': { pb: 0 },
          }}
        >
          {/* Header with Icon and Status */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'primary.main',
              }}
            >
              {ToolIcon}
              {tool.isNew && (
                <Chip
                  label="New"
                  size="small"
                  color="secondary"
                  sx={{
                    ml: 1,
                    height: 16,
                    fontSize: '0.625rem',
                  }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {hasVideos && (
                <Tooltip title="Tutorial Videos" arrow>
                  <Box
                    component="span"
                    role="button"
                    tabIndex={0}
                    onClick={handleVideoButtonClick}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleVideoButtonClick(e as any);
                      }
                    }}
                    aria-label="Tutorial Videos"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 44,
                      minHeight: 44,
                      width: 24,
                      height: 24,
                      p: 0.5,
                      color: 'primary.main',
                      cursor: 'pointer',
                      borderRadius: '50%',
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                      },
                      '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    <PlayCircleIcon sx={{ fontSize: 18 }} />
                  </Box>
                </Tooltip>
              )}

              {showStatus && (
                <Tooltip title={statusConfig.label} arrow>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 24,
                      minHeight: 24,
                      color: `${statusConfig.color}.main`,
                    }}
                  >
                    <StatusIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Tool Name */}
          <Typography
            variant={variant === 'compact' ? 'body2' : 'subtitle2'}
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: showDescription ? 0.5 : 0,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: variant === 'compact' ? 1 : 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {tool.name}
          </Typography>

          {/* Tool Description */}
          {showDescription && variant !== 'compact' && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.3,
              }}
            >
              {tool.description}
            </Typography>
          )}

          {/* Coming Soon Info */}
          {tool.status === 'coming_soon' && tool.estimatedLaunch && (
            <Typography
              variant="caption"
              sx={{
                color: 'info.main',
                mt: 0.5,
                fontWeight: 500,
              }}
            >
              Expected: {tool.estimatedLaunch}
            </Typography>
          )}

          {/* Premium Feature Indicator */}
          {tool.premiumFeature && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mt: 0.5,
              }}
            >
              <StarIcon
                sx={{
                  fontSize: 12,
                  color: 'primary.main',
                  mr: 0.5,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                }}
              >
                Premium
              </Typography>
            </Box>
          )}
        </CardContent>
      </CardActionArea>

      {/* Video Menu - for multiple videos */}
      {hasVideos && tool.tutorialVideos!.length > 1 && (
        <Menu
          anchorEl={videoMenuAnchor}
          open={Boolean(videoMenuAnchor)}
          onClose={handleVideoMenuClose}
          onClick={e => e.stopPropagation()}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {tool.tutorialVideos!.map(video => (
            <MenuItem
              key={video.videoId}
              onClick={() => handleVideoSelect(video.videoId, video.title)}
            >
              <ListItemIcon>
                <PlayCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={video.label} />
            </MenuItem>
          ))}
        </Menu>
      )}

      {/* Video Dialog */}
      {selectedVideo && (
        <YouTubeVideoDialog
          open
          onClose={handleVideoDialogClose}
          videoId={selectedVideo.videoId}
          title={selectedVideo.title}
        />
      )}
    </Card>
  );
};

export default ToolCard;
